import { z } from 'zod';

/**
 * First-class tag entity for `pending-tag-search-saved-views`.
 * Backed by migration 085 (`tags` + `flow_tags` join). Names are
 * case-insensitive unique inside an org (the SQL layer enforces
 * this via a generated `name_lower` column).
 *
 * Colour is optional — when NULL the picker assigns a
 * deterministic palette colour based on `hash(name) % palette.length`,
 * so a freshly-created `smoke` tag is always the same shade across
 * users and devices.
 */

/**
 * Tag-name validator. The rules mirror what the DSL parser
 * accepts as a bare identifier so a tag can always be referenced
 * in a query without quoting:
 *   • 1-40 chars
 *   • [a-z0-9-_] only (lowercase enforced post-trim by the API)
 *   • no leading / trailing dash
 *
 * Display copy in `@rynora/messages/tags`.
 */
export const TagNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/, {
    message:
      'Tag names must be lowercase, 1-40 characters, and contain only a-z, 0-9, hyphens, or underscores. Hyphens cannot lead or trail the name.',
  });

/**
 * Colour validator — accepts `#rgb` / `#rrggbb` / a small set of
 * named palette tokens (`slate`, `indigo`, `emerald`, etc). The
 * frontend treats the named tokens as a hint into a theme-bound
 * palette so dark / light mode contrast is consistent.
 */
export const TAG_COLOUR_TOKENS = [
  'slate',
  'indigo',
  'emerald',
  'amber',
  'rose',
  'cyan',
  'violet',
  'orange',
  'lime',
  'pink',
] as const;
export type TagColourToken = (typeof TAG_COLOUR_TOKENS)[number];

export const TagColourSchema = z.union([
  z.enum(TAG_COLOUR_TOKENS),
  z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, {
    message: 'Colour must be a #rgb / #rrggbb hex code or one of the named palette tokens.',
  }),
]);

export const TagSchema = z.object({
  id: z.string().uuid(),
  org_id: z.string().uuid(),
  name: TagNameSchema,
  colour: TagColourSchema.nullable(),
  description: z.string().max(240).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  /**
   * Server-aggregated count of flows the tag is applied to. The
   * `GET /api/tags` route fills this from `flow_tags` so the tag
   * manager can render usage counts without a per-row round-trip.
   * Optional because lighter endpoints (e.g. `POST /api/tags`'s
   * 201 response) skip the join.
   */
  flow_count: z.number().int().nonnegative().optional(),
});
export type Tag = z.infer<typeof TagSchema>;

export const CreateTagRequestSchema = z.object({
  name: TagNameSchema,
  colour: TagColourSchema.nullable().optional(),
  description: z.string().max(240).nullable().optional(),
});
export type CreateTagRequest = z.infer<typeof CreateTagRequestSchema>;

export const UpdateTagRequestSchema = z.object({
  name: TagNameSchema.optional(),
  colour: TagColourSchema.nullable().optional(),
  description: z.string().max(240).nullable().optional(),
});
export type UpdateTagRequest = z.infer<typeof UpdateTagRequestSchema>;

export const ListTagsResponseSchema = z.object({
  tags: z.array(TagSchema),
});
export type ListTagsResponse = z.infer<typeof ListTagsResponseSchema>;

/**
 * Body shape for `POST /api/flows/:id/tags` — caller supplies the
 * canonical set of tag ids the flow should carry. Server diffs
 * against the current `flow_tags` rows and applies the delta.
 *
 * Using "replace the whole set" semantics (instead of `add` /
 * `remove` actions) keeps the optimistic-UI logic trivial: the
 * frontend stores `flow.tags` as `Tag[]` and PUTs the array
 * verbatim. No concurrency surprises.
 */
export const ApplyFlowTagsRequestSchema = z.object({
  tag_ids: z.array(z.string().uuid()).max(50),
});
export type ApplyFlowTagsRequest = z.infer<typeof ApplyFlowTagsRequestSchema>;

/**
 * Deterministic palette assignment for tags whose `colour` is
 * NULL. Mirrored on both sides of the wire so a freshly-created
 * tag without an explicit colour renders identically on the
 * server-side digest summaries (e.g. weekly email) and the
 * client-side picker.
 *
 * The hash is intentionally not crypto-grade — it just needs to
 * be stable and well-distributed. djb2 is plenty.
 */
export function deriveTagColour(name: string): TagColourToken {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 33) ^ name.charCodeAt(i);
  }
  const idx = Math.abs(hash) % TAG_COLOUR_TOKENS.length;
  return TAG_COLOUR_TOKENS[idx];
}
